import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { getBrowserId } from "./browserId";
import { broadcastAuthChange, onAuthChange } from "./authChannel";
import { clearToken } from "./nativeTokenStorage";

const AuthCtx = createContext(null);

// XX.291 Step 1 — Identity-adjacent localStorage keys that MUST be
// dropped on explicit sign-out so the next visitor lands in a
// genuinely anonymous state. Deliberately excludes:
//   · gully_browser_id   — stable device identity (used by claim math)
//   · gully_attribution  — attribution predates the account
//   · gully_utm, gully_returning_v1, gully_internal — analytics only
// The `gully_pending_uploads` key is cleared; any in-flight PUT that
// has already been POSTed to the server is already durable server-side.
// A queued-but-never-sent trace is intentionally dropped — the previous
// user's session should not resume against the next visitor.
const _IDENTITY_ADJACENT_STORAGE_KEYS = [
  "gully_web_session_id",
  "gully_pending_continuity_session",
  "gully_last_identity_trigger",
  "g_high_intent_plus",
  "gully_plus_purchase_entry",
  "gully_pending_uploads",
];

export function _clearIdentityAdjacentStorage() {
  for (const key of _IDENTITY_ADJACENT_STORAGE_KEYS) {
    try { localStorage.removeItem(key); } catch { /* private mode / quota */ }
  }
}

// Iter XX.26 — one-time older-conversations prompt payload. We
// stash it in module scope so the Chat page can read it after
// AuthProvider finishes its post-login claim. Cleared once the
// player answers the prompt (or dismisses it).
let _pendingOlderPrompt = null;
export function readPendingOlderPrompt() {
  const v = _pendingOlderPrompt; _pendingOlderPrompt = null; return v;
}

async function _runClaim({ includeOlder = false } = {}) {
  try {
    const { data } = await api.post("/web/sessions/claim", {
      browser_id: getBrowserId(),
      include_older: !!includeOlder,
    });
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authTick, setAuthTick] = useState(0);   // bump → tabs re-render

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // XX.291 Step 2 — Session-ownership reconciliation. If this
      // browser has a cached gully_web_session_id that the newly-
      // authenticated user does NOT own on the server, drop the
      // local id so the next chat POST starts a fresh session. We
      // never mutate, merge, delete or re-parent any history — the
      // orphaned session stays intact under whoever actually owns
      // it. Failure is fully non-fatal: bad network / 5xx / stale
      // token → we leave the local id alone (best-effort).
      try {
        const localSid = (() => {
          try { return localStorage.getItem("gully_web_session_id"); }
          catch { return null; }
        })();
        if (localSid && data && data.user_id) {
          const { data: mine } = await api.get(
            `/web/sessions/mine?session_id=${encodeURIComponent(localSid)}`
          );
          if (mine && !mine.owned_by_me) {
            try { localStorage.removeItem("gully_web_session_id"); }
            catch { /* noop */ }
          }
        }
      } catch { /* non-fatal — keep local id if we can't verify */ }
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Iter XX.26 — cross-tab sync. When another tab logs in/out, this
  // tab refreshes its auth state without a manual reload.
  useEffect(() => {
    return onAuthChange(async (evt) => {
      if (!evt || !evt.kind) return;
      if (evt.kind === "logout") {
        // Clear our local token store and drop the user; do NOT
        // re-broadcast (the origin tab already did). On native this
        // clears the Keychain / EncryptedSharedPreferences entry; on
        // web this removes localStorage (XX.177).
        try { await clearToken(); } catch { /* noop */ }
        // XX.291 Step 1 — mirror the origin-tab cleanup so every
        // open tab lands in the same anonymous state.
        _clearIdentityAdjacentStorage();
        setUser(null);
        setAuthTick((n) => n + 1);
        return;
      }
      if (evt.kind === "login") {
        const u = await checkAuth();
        if (u) {
          const c = await _runClaim();
          if (c && c.sessions_skipped_older > 0) {
            _pendingOlderPrompt = c;
          }
          setAuthTick((n) => n + 1);
        }
      }
    });
  }, [checkAuth]);

  // Wraps caller-provided setUser so login flows automatically
  // broadcast + claim.
  const setUserAndBroadcast = useCallback(async (u) => {
    setUser(u);
    if (u) {
      broadcastAuthChange("login");
      const c = await _runClaim();
      if (c && c.sessions_skipped_older > 0) {
        _pendingOlderPrompt = c;
      }
    }
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { await clearToken(); } catch { /* noop */ }
    // XX.291 Step 1 — clear identity-adjacent localStorage BEFORE
    // dropping the user, so any late render this tab does can never
    // read a stale gully_web_session_id that pointed at the previous
    // user's conversation. Preserves gully_browser_id + attribution.
    _clearIdentityAdjacentStorage();
    // Iter XX.230 — Null the RevenueCat last-good-monthly-package
    // JS cache so the next signed-in player never sees the previous
    // user's cached offering. Best-effort import (test envs may
    // mock this module away).
    try {
      const rc = await import("./revenueCat");
      rc.clearMonthlyPackageCacheOnLogout?.();
    } catch { /* noop */ }
    setUser(null);
    broadcastAuthChange("logout");
  };

  return (
    <AuthCtx.Provider value={{
      user, setUser: setUserAndBroadcast, loading, checkAuth, logout,
      authTick,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Iter XX.26 — public claim helper for pages (e.g. Chat.jsx's
// "Add older conversations" modal confirmation).
export async function claimOlderConversations() {
  return _runClaim({ includeOlder: true });
}
